import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { tv } from "tailwind-variants";

const navItemVariants = tv({
  base: [
    "flex h-[34px] w-[272px] items-center justify-between",
    "py-[11px] pl-4 pr-8 shadow-[0px_4px_15px_rgba(0,0,0,0.1)]",
    "rounded-[0px_30px_30px_0px] transition-colors",
    "hover:opacity-90",
  ],
  variants: {
    variant: {
      default: "bg-custom-white",
      active: "bg-variable-collection-main",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const iconVariants = tv({
  base: "size-[18px]",
  variants: {
    variant: {
      default: "text-variable-collection-dark-gray",
      active: "text-white",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const textVariants = tv({
  base: "text-[20px] font-semibold",
  variants: {
    variant: {
      default: "text-text-subtitle",
      active: "text-white",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface NavItemProps {
  to?: string;
  icon: ReactNode;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}

export const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  isActive = false,
  onClick,
  className,
}) => {
  const variant = isActive ? "active" : "default";
  const combinedClassName = navItemVariants({ variant, className });

  const content = (
    <div className="flex items-center gap-1">
      <span className={iconVariants({ variant })}>{icon}</span>
      <span className={textVariants({ variant })}>{label}</span>
    </div>
  );

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={combinedClassName}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={combinedClassName}
      aria-pressed={isActive}
    >
      {content}
    </button>
  );
};
